import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs';
import { AccountWallActions } from '@states/account-wall/account-wall.actions';
import { AccountWallService } from '../../services/account-wall.service';
import { UtilsService } from '../../edge/utils.service';
import { LoggerService } from '../../services/logger.service';
import { ContextualLoggerService } from '../../services/contextual-logger.service';
import { SharedActions } from '@states/shared/shared.action-types';
import { accountWallFeature } from '@states/account-wall/account-wall.feature';

const context = 'AccountWallEffect';
const createLogger = (baseLogger: LoggerService) =>
  new ContextualLoggerService(baseLogger, context);

export const getAllWalls$ = createEffect(
  (
    actions$ = inject(Actions),
    accountWallService = inject(AccountWallService),
    store$ = inject(Store),
    utilsService = inject(UtilsService),
    baseLogger = inject(LoggerService),
  ) => {
    const logger = createLogger(baseLogger);
    return actions$.pipe(
      ofType(AccountWallActions.getAllWalls),
      withLatestFrom(store$.select(accountWallFeature.selectQueryParams)),
      withLatestFrom(store$.select(accountWallFeature.selectLatestTs)),
      switchMap(([[{ accountId }, queryParams], latestTs]) =>
        accountWallService
          .getAllWallsForAccount(accountId, queryParams, latestTs)
          .pipe(
            map((walls) =>
              AccountWallActions.getAllWallsSuccess({
                walls,
                isFirstPage: latestTs === null,
              }),
            ),
            catchError((err) => {
              logger.error(utilsService.errMessage(err));
              return [
                AccountWallActions.getAllWallsFail(),
                SharedActions.showMessage({ error: utilsService.errMessage(err) }),
              ];
            }),
          ),
      ),
    );
  },
  { functional: true },
);

export const getNextPage$ = createEffect(
  (actions$ = inject(Actions), store$ = inject(Store)) =>
    actions$.pipe(
      ofType(AccountWallActions.getNextPage),
      withLatestFrom(store$.select(accountWallFeature.selectCurrentAccountId)),
      filter(([, accountId]) => !!accountId),
      map(([, accountId]) =>
        AccountWallActions.getAllWalls({ accountId: accountId! }),
      ),
    ),
  { functional: true },
);

export const onQueryParamsChanged$ = createEffect(
  (actions$ = inject(Actions), store$ = inject(Store)) =>
    actions$.pipe(
      ofType(AccountWallActions.onQueryParamsChanged),
      withLatestFrom(store$.select(accountWallFeature.selectCurrentAccountId)),
      filter(([, accountId]) => !!accountId),
      mergeMap(([, accountId]) => [
        AccountWallActions.resetPaging(),
        AccountWallActions.getAllWalls({ accountId: accountId! }),
      ]),
    ),
  { functional: true },
);

export const filterChangeTriggersReload$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        AccountWallActions.setSearchQueryFilter,
        AccountWallActions.setCameraQueryFilter,
        AccountWallActions.setTagsFilter,
        AccountWallActions.removeCameraFilter,
        AccountWallActions.removeQueryFilter,
        AccountWallActions.removeTagsFilter,
      ),
      map(() => AccountWallActions.onQueryParamsChanged()),
    ),
  { functional: true },
);

export const addWallToAccount$ = createEffect(
  (
    actions$ = inject(Actions),
    accountWallService = inject(AccountWallService),
    utilsService = inject(UtilsService),
    baseLogger = inject(LoggerService),
  ) => {
    const logger = createLogger(baseLogger);
    return actions$.pipe(
      ofType(AccountWallActions.addWallToAccount),
      switchMap(({ accountId, wallId }) =>
        accountWallService.addWallToAccount(accountId, wallId).pipe(
          map(() => AccountWallActions.addWallToAccountSuccess({ wallId })),
          catchError((err) => {
            logger.error(utilsService.errMessage(err));
            return [
              AccountWallActions.addWallToAccountFail(),
              SharedActions.showMessage({ error: utilsService.errMessage(err) }),
            ];
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const removeWallFromAccount$ = createEffect(
  (
    actions$ = inject(Actions),
    accountWallService = inject(AccountWallService),
    utilsService = inject(UtilsService),
    baseLogger = inject(LoggerService),
  ) => {
    const logger = createLogger(baseLogger);
    return actions$.pipe(
      ofType(AccountWallActions.removeWallFromAccount),
      switchMap(({ accountId, wallId }) =>
        accountWallService.removeWallFromAccount(accountId, wallId).pipe(
          map(() => AccountWallActions.removeWallFromAccountSuccess({ wallId })),
          catchError((err) => {
            logger.error(utilsService.errMessage(err));
            return [
              AccountWallActions.removeWallFromAccountFail(),
              SharedActions.showMessage({ error: utilsService.errMessage(err) }),
            ];
          }),
        ),
      ),
    );
  },
  { functional: true },
);
